import { useEffect } from 'react'

export const bodyTranslationCallback = (mutations: MutationRecord[]) => {
  /*
  The library we are using to manage cookies directly sets transform styles on the body tag to shunt the page down below
  the cookie banner. It also doesn't clean up properly after itself and leaves a translateY(0px) set on the body even
  after the cookies banner has been removed. Unfortunately, there is a known 'quirk' in most browser renderers that,
  when a transform is set on the body, all css properties that are relative to a viewport (e.g. display:fixed) cease to
  work. Full explanation can be found at  http://meyerweb.com/eric/thoughts/2011/09/12/un-fixing-fixed-elements-with-css-transforms/

  We need display:fixed to display our order details bottom banner, but for legal reasons cannot interfere with the
  cookie banner. The half-way solution below is to observe body style mutations and, when a translationY of 0 has been
  set (ie: the cookie banner has gone) remove that property completely from the body styles, allowing viewport dependant
  properties to function correctly again
   */
  mutations
    .filter((m) => m.attributeName === 'style') // This is a body style change
    .map((m) => m.target as HTMLElement)
    .filter((target) => target.style.transform) // The transform style attribute has been set
    .forEach((target) => {
      const targetStyles = target.style
      const translateMatch = targetStyles.transform.match(/translateY\(([0-9]+)(?:px)?\)/)
      if (translateMatch) {
        const translateBy = parseInt(translateMatch[1], 10)
        if (translateBy === 0) {
          targetStyles.transform = ''
        }
      }
    })
}

const useBodyReset = () => {
  useEffect(() => {
    // See the comment in bodyTranslationCallback for why all this is needed
    // Create a property observer to watch for changes to body styles
    const observer = new MutationObserver(bodyTranslationCallback)
    observer.observe(document.body, { attributes: true })
    // Disconnect the observer on component unmount
    return () => observer.disconnect()
  }, [])
}

export default useBodyReset
